/** Saved presentation definitions, never telemetry measurements. Trace order is significant. */
export interface SavedTraceViewDefinition {
  schemaVersion: 1;
  name: string;
  /** Ordered channel keys; each key may appear at most once. */
  traceKeys: string[];
  labels?: Record<string, string>;
  pairs?: Record<string, string>;
  units?: Record<string, string>;
}

export interface SavedTraceView extends SavedTraceViewDefinition {
  id: string;
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export type SavedTraceViewSummary = Pick<SavedTraceView, 'id' | 'schemaVersion' | 'name' | 'revision' | 'createdAt' | 'updatedAt'> & { traceCount: number };
export interface SavedTraceViewList {
  items: SavedTraceViewSummary[];
  total: number;
  nextCursor: string | null;
}
export const SAVED_TRACE_VIEW_LIMITS = { views: 100, traces: 256, bytes: 96 * 1024, page: 50 } as const;

/** Saved-view editors address traces by key, so duplicate entries are ambiguous. */
export function hasUniqueSavedTraceKeys(traceKeys: readonly string[]): boolean {
  return new Set(traceKeys).size === traceKeys.length;
}

/** Trace keys are used to index plain objects throughout the editor. */
export function isSafeSavedTraceKey(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= 80
    && value !== 'prototype' && !(value in Object.prototype);
}

/** Each chart has two distinct selected channels; a channel cannot belong to two charts. */
export function hasValidSavedTracePairs(traceKeys: readonly string[], pairs?: Record<string, string>): boolean {
  const selected = new Set(traceKeys);
  const paired = new Set<string>();
  for (const [primary, secondary] of Object.entries(pairs ?? {})) {
    if (primary === secondary || !selected.has(primary) || !selected.has(secondary)
      || paired.has(primary) || paired.has(secondary)) return false;
    paired.add(primary);
    paired.add(secondary);
  }
  return true;
}
